import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
  Button,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import {
  ScatterChart as RechartsScatterChart,
  Scatter as RechartsScatter,
  XAxis as RechartsXAxis,
  YAxis as RechartsYAxis,
  CartesianGrid as RechartsCartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine as RechartsReferenceLine,
} from "recharts";

import {
  VerticalBarChart,
  LineChart,
} from "../../../../../shared/components/charts";
import {
  DashboardCard,
  DashboardCardSkeleton,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  CohortGuard,
} from "../../../../../shared/components";
import {
  useMetricsComparison,
  useMetricsTrends,
  useMetricsStats,
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
} from "../../../../../shared/hooks";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";

// ─── Metric catalogue ────────────────────────────────────────────────────────

const METRIC_OPTIONS: Array<{ label: string; value: string; unit: string; group: string }> = [
  // Recovery
  { label: "Recovery Score", value: "recovery_score", unit: "%", group: "Recovery" },
  { label: "Recovery", value: "recovery", unit: "%", group: "Recovery" },
  { label: "HRV", value: "hrv", unit: "ms", group: "Recovery" },
  { label: "Average HRV", value: "average_hrv", unit: "ms", group: "Recovery" },
  { label: "Stress Score", value: "stress_score", unit: "", group: "Recovery" },
  { label: "Stress Recovery Balance", value: "stress_recovery_balance", unit: "", group: "Recovery" },
  { label: "High Stress Days", value: "high_stress_days", unit: "days", group: "Recovery" },
  // Sleep
  { label: "Sleep", value: "sleep", unit: "%", group: "Sleep" },
  { label: "Sleep Performance", value: "sleep_performance", unit: "%", group: "Sleep" },
  { label: "Sleep Efficiency", value: "sleep_efficiency", unit: "%", group: "Sleep" },
  { label: "Sleep Consistency", value: "sleep_consistency", unit: "%", group: "Sleep" },
  { label: "Sleep Stress", value: "sleep_stress", unit: "%", group: "Sleep" },
  { label: "Sleep Debt", value: "sleep_debt", unit: "hrs", group: "Sleep" },
  { label: "Hours of Sleep", value: "hours_of_sleep", unit: "hrs", group: "Sleep" },
  { label: "Hours of Sleep Needed", value: "hours_of_sleep_needed", unit: "hrs", group: "Sleep" },
  { label: "Nap Duration", value: "nap_duration", unit: "hrs", group: "Sleep" },
  { label: "Awake Stage", value: "sleep_stage_awake", unit: "hr", group: "Sleep" },
  { label: "Light Sleep Stage", value: "sleep_stage_light", unit: "hr", group: "Sleep" },
  { label: "REM Sleep Stage", value: "sleep_stage_rem", unit: "hr", group: "Sleep" },
  { label: "Deep Sleep Stage", value: "sleep_stage_deep", unit: "hr", group: "Sleep" },
  // Strain & Activity
  { label: "Strain", value: "strain", unit: "", group: "Strain & Activity" },
  { label: "Day Strain", value: "day_strain", unit: "", group: "Strain & Activity" },
  { label: "Active Time", value: "active_time", unit: "min", group: "Strain & Activity" },
  { label: "Calories Burned", value: "calories_burn", unit: "kcal", group: "Strain & Activity" },
  { label: "Cardio Load", value: "cardio_load", unit: "kJ", group: "Strain & Activity" },
  { label: "Steps", value: "steps", unit: "", group: "Strain & Activity" },
  // Heart Rate & Vitals
  { label: "Resting Heart Rate", value: "resting_heart_rate", unit: "bpm", group: "Heart Rate & Vitals" },
  { label: "Heart Rate", value: "heart_rate", unit: "bpm", group: "Heart Rate & Vitals" },
  { label: "Avg Heart Rate", value: "avg_heart_rate", unit: "bpm", group: "Heart Rate & Vitals" },
  { label: "Max Heart Rate", value: "max_heart_rate", unit: "bpm", group: "Heart Rate & Vitals" },
  { label: "Respiratory Rate", value: "respiratory_rate", unit: "rpm", group: "Heart Rate & Vitals" },
  { label: "SpO2", value: "spo2", unit: "%", group: "Heart Rate & Vitals" },
  { label: "Temperature", value: "temperature", unit: "°C", group: "Heart Rate & Vitals" },
  // Programme
  { label: "Participation Rate", value: "participation_rate", unit: "%", group: "Programme" },
  { label: "Completion Rate", value: "completion_rate", unit: "%", group: "Programme" },
  { label: "High Risk Employees", value: "high_risk_employees", unit: "%", group: "Programme" },
  { label: "Medium Risk Employees", value: "medium_risk_employees", unit: "%", group: "Programme" },
  { label: "Low Risk Employees", value: "low_risk_employees", unit: "%", group: "Programme" },
  { label: "Pace of Aging", value: "pace_of_aging", unit: "", group: "Programme" },
];

const metricLabel = (key: string) =>
  METRIC_OPTIONS.find((m) => m.value === key)?.label ?? key;

const metricUnit = (key: string) =>
  METRIC_OPTIONS.find((m) => m.value === key)?.unit ?? "";

/**
 * Maps frontend metric UI keys to the comparison API's supported metric keys.
 * The comparison endpoint only supports a fixed set; many UI keys are aliases.
 */
const COMPARISON_KEY_MAP: Record<string, string> = {
  hours_of_sleep: "sleep_duration",
  hours_of_sleep_needed: "sleep_duration",
  nap_duration: "sleep_duration",
  sleep_performance: "sleep_score",
  sleep: "sleep_score",
  sleep_efficiency: "sleep_score",
  sleep_consistency: "sleep_score",
  sleep_stress: "overall_stress",
  average_hrv: "hrv",
  recovery: "recovery_score",
  heart_rate: "resting_heart_rate",
  avg_heart_rate: "resting_heart_rate",
  max_heart_rate: "resting_heart_rate",
  stress_score: "overall_stress",
  stress_recovery_balance: "overall_stress",
  day_strain: "strain",
};

/** The set of metric keys the comparison API natively supports. */
const COMPARISON_SUPPORTED = new Set([
  "sleep_score",
  "overall_stress",
  "recovery_score",
  "hrv",
  "strain",
  "sleep_duration",
  "resting_heart_rate",
  "respiratory_rate",
]);

/**
 * Returns the comparison API key for a UI metric key, or null if not mappable.
 */
const toComparisonKey = (uiKey: string): string | null => {
  if (COMPARISON_SUPPORTED.has(uiKey)) return uiKey;
  const mapped = COMPARISON_KEY_MAP[uiKey];
  return mapped && COMPARISON_SUPPORTED.has(mapped) ? mapped : null;
};

// ─── Segmentation options ─────────────────────────────────────────────────────

type GroupByApiValue =
  | "department"
  | "age_group"
  | "gender"
  | "shift_pattern"
  | "user";

const SEGMENT_OPTIONS: Array<{ label: string; value: GroupByApiValue }> = [
  { label: "Department / Role", value: "department" },
  { label: "Age Group", value: "age_group" },
  { label: "Gender", value: "gender" },
  { label: "Shift Pattern", value: "shift_pattern" },
  { label: "Individual Users", value: "user" },
];

// ─── Chart view tabs ─────────────────────────────────────────────────────────

type ViewTab = "combined" | "segmented";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Pearson correlation coefficient between two equal-length numeric arrays.
 * Returns null when there are fewer than 2 valid pairs.
 */
function pearson(xs: number[], ys: number[]): number | null {
  const pairs = xs
    .map((x, i) => [x, ys[i]] as [number, number])
    .filter(([x, y]) => isFinite(x) && isFinite(y));
  if (pairs.length < 2) return null;
  const n = pairs.length;
  const meanX = pairs.reduce((s, [x]) => s + x, 0) / n;
  const meanY = pairs.reduce((s, [, y]) => s + y, 0) / n;
  const num = pairs.reduce((s, [x, y]) => s + (x - meanX) * (y - meanY), 0);
  const denX = Math.sqrt(pairs.reduce((s, [x]) => s + (x - meanX) ** 2, 0));
  const denY = Math.sqrt(pairs.reduce((s, [, y]) => s + (y - meanY) ** 2, 0));
  if (denX === 0 || denY === 0) return null;
  return num / (denX * denY);
}

/** Format a percentage change string for display. */
function formatChange(change: string | undefined): {
  text: string;
  positive: boolean | null;
} {
  if (!change) return { text: "", positive: null };
  const trimmed = change.trim();
  const positive = trimmed.startsWith("+");
  const negative = trimmed.startsWith("-");
  return {
    text: trimmed,
    positive: positive ? true : negative ? false : null,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SummaryStatCardProps {
  label: string;
  value: number | null | undefined;
  unit: string;
  change?: string;
  color: string;
}

const SummaryStatCard: React.FC<SummaryStatCardProps> = ({
  label,
  value,
  unit,
  change,
  color,
}) => {
  const theme = useTheme();
  const { text, positive } = formatChange(change);
  const trendColor =
    positive === true
      ? theme.palette.success.main
      : positive === false
        ? theme.palette.error.main
        : theme.palette.text.secondary;
  const TrendIcon =
    positive === true
      ? TrendingUpIcon
      : positive === false
        ? TrendingDownIcon
        : TrendingFlatIcon;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>
          {value != null ? Math.round(value * 10) / 10 : "—"}
        </Typography>
        {unit && (
          <Typography variant="caption" color="text.secondary">
            {unit}
          </Typography>
        )}
      </Box>
      {text && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: trendColor }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: "inherit" }}>
            {text}
          </Typography>
          <TrendIcon sx={{ fontSize: "0.75rem" }} />
          <Typography variant="caption" color="text.secondary">
            vs prev. period
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const MeasureComparisonInsights: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised, selectedClientAnonymised } = useClientScope();

  // Hide "Individual Users" when the selected client has anonymisation enabled.
  const availableSegmentOptions = selectedClientAnonymised
    ? SEGMENT_OPTIONS.filter((s) => s.value !== "user")
    : SEGMENT_OPTIONS;

  // ── Persistent state ─────────────────────────────────────────────────────
  const { dateRange, setDateRange } = usePersistedDateRange();
  const { filters, setFilters } = usePersistedFilters({
    storageKey: "peak4.admin.measure-comparison.filters.v1",
  });

  // ── Local UI state ────────────────────────────────────────────────────────
  const [metricA, setMetricA] = useState<string>("sleep_efficiency");
  const [metricB, setMetricB] = useState<string>("recovery_score");
  const [segmentBy, setSegmentBy] = useState<GroupByApiValue>("department");
  const [selectedSegmentValues, setSelectedSegmentValues] = useState<string[]>([]);
  const [activeView, setActiveView] = useState<ViewTab>("combined");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Reset segmentBy if the current selection is no longer available (e.g. anonymisation toggled on).
  useEffect(() => {
    if (segmentBy === "user" && selectedClientAnonymised) {
      setSegmentBy("department");
      setSelectedSegmentValues([]);
    }
  }, [segmentBy, selectedClientAnonymised]);

  const activeFiltersCount = getAppliedFilterCount(filters);

  // ── Derived params ────────────────────────────────────────────────────────
  const filterParams = useMemo(
    () => ({
      ...buildFilterParams(filters, dateRange, effectiveClientId),
      bypass_anonymisation: isCentralAdmin,
    }),
    [filters, dateRange, effectiveClientId, isCentralAdmin]
  );

  const granularity = useMemo(() => calculateGranularity(dateRange), [dateRange]);

  const metricsForQuery = useMemo(() => [metricA, metricB], [metricA, metricB]);

  // Comparison API uses different keys than the UI for some metrics
  const comparisonKeyA = useMemo(() => toComparisonKey(metricA), [metricA]);
  const comparisonKeyB = useMemo(() => toComparisonKey(metricB), [metricB]);
  const comparisonMetrics = useMemo(() => {
    const keys = [comparisonKeyA, comparisonKeyB].filter(Boolean) as string[];
    return Array.from(new Set(keys));
  }, [comparisonKeyA, comparisonKeyB]);

  // Clear selected segment values whenever the dimension changes
  const handleSegmentByChange = (val: GroupByApiValue) => {
    setSegmentBy(val);
    setSelectedSegmentValues([]);
  };

  // ── Data queries ──────────────────────────────────────────────────────────
  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  const {
    data: statsData,
    isLoading: statsLoading,
  } = useMetricsStats({
    ...filterParams,
    metrics: metricsForQuery,
  });

  const {
    data: trendsData,
    isLoading: trendsLoading,
    error: trendsError,
  } = useMetricsTrends({
    ...filterParams,
    metrics: metricsForQuery,
    granularity,
  });

  // Segmented comparison — used for both bar chart and scatter plot.
  // Uses comparison-API-normalised keys (e.g. hours_of_sleep → sleep_duration).
  const {
    data: comparisonData,
    isLoading: comparisonLoading,
    error: comparisonError,
  } = useMetricsComparison({
    ...filterParams,
    group_by: segmentBy,
    metrics: comparisonMetrics.length ? comparisonMetrics : metricsForQuery,
  });

  // ── Derived data ──────────────────────────────────────────────────────────

  const metricARange = useMemo(() => {
    const minMap = overallRangeQuery.data?.data?.metric_min ?? {};
    const maxMap = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      min: typeof minMap[metricA] === "number" ? (minMap[metricA] as number) : undefined,
      max: typeof maxMap[metricA] === "number" ? (maxMap[metricA] as number) : undefined,
    };
  }, [overallRangeQuery.data, metricA]);

  const statA = statsData?.data?.[metricA];
  const statB = statsData?.data?.[metricB];

  const trendChartData = useMemo(() => {
    if (!trendsData) return [];
    return transformTrendsData(trendsData, [metricA, metricB]);
  }, [trendsData, metricA, metricB]);

  // All available group labels from the loaded comparison data
  const availableSegmentValues = useMemo(() => {
    if (!comparisonData?.data?.groups) return [];
    return comparisonData.data.groups.map((g) => g.group_label).filter(Boolean);
  }, [comparisonData]);

  // Groups after applying the segment value filter (empty selection = show all)
  // Also filter out segments with no data for either selected metric (privacy: insufficient data)
  // Use comparison API keys when reading values from comparison response
  const ckA = comparisonKeyA ?? metricA;
  const ckB = comparisonKeyB ?? metricB;

  const filteredGroups = useMemo(() => {
    if (!comparisonData?.data?.groups) return [];
    let groups = comparisonData.data.groups;
    if (selectedSegmentValues.length > 0) {
      groups = groups.filter((g) => selectedSegmentValues.includes(g.group_label));
    }
    // Filter out segments where both metrics lack data (insufficient data points)
    return groups.filter(
      (g) => g.values[ckA] != null || g.values[ckB] != null
    );
  }, [comparisonData, selectedSegmentValues, ckA, ckB]);

  // Track how many segments were hidden due to insufficient data
  const hiddenSegmentCount = useMemo(() => {
    if (!comparisonData?.data?.groups) return 0;
    let groups = comparisonData.data.groups;
    if (selectedSegmentValues.length > 0) {
      groups = groups.filter((g) => selectedSegmentValues.includes(g.group_label));
    }
    return groups.filter(
      (g) => g.values[ckA] == null && g.values[ckB] == null
    ).length;
  }, [comparisonData, selectedSegmentValues, ckA, ckB]);

  const barChartData = useMemo(() => {
    if (!filteredGroups.length) return [];
    const segLabel =
      availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;
    return filteredGroups.map((group) => ({
      [segLabel]: group.group_label,
      [metricLabel(metricA)]: group.values[ckA] ?? 0,
      [metricLabel(metricB)]: group.values[ckB] ?? 0,
    }));
  }, [filteredGroups, segmentBy, metricA, metricB, ckA, ckB]);

  /**
   * Scatter data: each group becomes one point.
   * x = Metric A average for the group, y = Metric B average.
   * When grouped by "user", each point is a single person.
   */
  const scatterData = useMemo(() => {
    return filteredGroups
      .filter((g) => g.values[ckA] != null && g.values[ckB] != null)
      .map((g, i) => ({
        id: i,
        x: g.values[ckA] as number,
        y: g.values[ckB] as number,
        label: g.group_label,
      }));
  }, [filteredGroups, ckA, ckB]);

  /** Pearson correlation across visible scatter points. */
  const correlation = useMemo(() => {
    if (scatterData.length < 2) return null;
    return pearson(
      scatterData.map((p) => p.x),
      scatterData.map((p) => p.y)
    );
  }, [scatterData]);

  /**
   * Linear regression slope/intercept for annotation display.
   */
  const regression = useMemo(() => {
    if (scatterData.length < 2) return null;
    const xs = scatterData.map((p) => p.x);
    const ys = scatterData.map((p) => p.y);
    const n = xs.length;
    const meanX = xs.reduce((s, v) => s + v, 0) / n;
    const meanY = ys.reduce((s, v) => s + v, 0) / n;
    const slopeNum = xs.reduce((s, x, i) => s + (x - meanX) * (ys[i] - meanY), 0);
    const slopeDen = xs.reduce((s, x) => s + (x - meanX) ** 2, 0);
    if (slopeDen === 0) return null;
    const slope = slopeNum / slopeDen;
    const intercept = meanY - slope * meanX;
    return { slope, intercept };
  }, [scatterData]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleViewChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: ViewTab | null
  ) => {
    if (newValue !== null) setActiveView(newValue);
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderTrendView = (height = 420) => {
    if (trendsLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height }}>
          <CircularProgress />
        </Box>
      );
    }
    if (trendsError) {
      return <Alert severity="error">Failed to load trend data. Please try again.</Alert>;
    }
    if (trendChartData.length === 0) {
      return <Alert severity="info">No trend data available for the selected period.</Alert>;
    }

    return (
      <LineChart
        data={trendChartData}
        xAxisKey="label"
        series={[
          {
            dataKey: metricA,
            label: metricLabel(metricA),
            color: theme.palette.info.main,
            area: true,
          },
          {
            dataKey: metricB,
            label: metricLabel(metricB),
            color: theme.palette.success.main,
            area: true,
          },
        ]}
        height={height}
        yAxisMin={0}
        showMinMaxLines
        referenceMin={metricARange.min}
        referenceMax={metricARange.max}
      />
    );
  };

  const renderScatterView = (height = 420) => {
    if (comparisonLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height }}>
          <CircularProgress />
        </Box>
      );
    }
    if (comparisonError) {
      return (
        <Alert severity="error">Failed to load comparison data. Please try again.</Alert>
      );
    }
    if (!comparisonKeyA || !comparisonKeyB) {
      const unsupported = [
        !comparisonKeyA ? metricLabel(metricA) : null,
        !comparisonKeyB ? metricLabel(metricB) : null,
      ]
        .filter(Boolean)
        .join(" and ");
      return (
        <Alert severity="warning">
          <strong>{unsupported}</strong>{" "}
          {(!comparisonKeyA && !comparisonKeyB) ? "are" : "is"} not available for correlation
          comparison. Try selecting metrics from: Recovery, HRV, Sleep, Strain, Heart Rate,
          or Respiratory Rate.
        </Alert>
      );
    }
    if (scatterData.length === 0) {
      return (
        <Alert severity="info">
          No data points to plot. Try expanding the date range or changing the segment
          dimension.
        </Alert>
      );
    }

    const segLabel =
      availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;

    const regressionPoints = regression
      ? (() => {
          const xs = scatterData.map((p) => p.x);
          const minX = Math.min(...xs);
          const maxX = Math.max(...xs);
          return [
            { x: minX, ry: regression.slope * minX + regression.intercept },
            { x: maxX, ry: regression.slope * maxX + regression.intercept },
          ];
        })()
      : null;

    return (
      <Box>
        {hiddenSegmentCount > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {hiddenSegmentCount} segment{hiddenSegmentCount !== 1 ? "s were" : " was"} hidden due to insufficient data.
          </Alert>
        )}
        {correlation !== null && (
          <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">
              Pearson r:
            </Typography>
            <Chip
              label={correlation.toFixed(3)}
              size="small"
              color={
                Math.abs(correlation) >= 0.7
                  ? "success"
                  : Math.abs(correlation) >= 0.4
                    ? "warning"
                    : "default"
              }
            />
            <Typography variant="caption" color="text.secondary">
              {Math.abs(correlation) >= 0.7
                ? "Strong"
                : Math.abs(correlation) >= 0.4
                  ? "Moderate"
                  : "Weak"}{" "}
              {correlation >= 0 ? "positive" : "negative"} ·{" "}
              {segLabel}
            </Typography>
          </Box>
        )}
        <ResponsiveContainer width="100%" height={height - 60}>
          <RechartsScatterChart margin={{ top: 16, right: 32, bottom: 48, left: 48 }}>
            <RechartsCartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <RechartsXAxis
              type="number"
              dataKey="x"
              name={metricLabel(metricA)}
              tick={{ fontSize: 11 }}
              tickLine={false}
              label={{
                value: `${metricLabel(metricA)}${metricUnit(metricA) ? ` (${metricUnit(metricA)})` : ""}`,
                position: "insideBottom",
                offset: -12,
                fontSize: 12,
                fill: theme.palette.text.secondary,
              }}
            />
            <RechartsYAxis
              type="number"
              dataKey="y"
              name={metricLabel(metricB)}
              tick={{ fontSize: 11 }}
              tickLine={false}
              label={{
                value: `${metricLabel(metricB)}${metricUnit(metricB) ? ` (${metricUnit(metricB)})` : ""}`,
                angle: -90,
                position: "insideLeft",
                offset: 12,
                fontSize: 12,
                fill: theme.palette.text.secondary,
              }}
            />
            <RechartsTooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={({ payload }) => {
                if (!payload?.length) return null;
                const pt = payload[0]?.payload as typeof scatterData[number];
                return (
                  <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1, fontSize: 12 }}>
                    <Typography variant="caption" fontWeight={700}>{pt.label}</Typography>
                    <Box>{metricLabel(metricA)}: <strong>{pt.x.toFixed(1)}{metricUnit(metricA) ? ` ${metricUnit(metricA)}` : ""}</strong></Box>
                    <Box>{metricLabel(metricB)}: <strong>{pt.y.toFixed(1)}{metricUnit(metricB) ? ` ${metricUnit(metricB)}` : ""}</strong></Box>
                  </Box>
                );
              }}
            />
            <RechartsScatter
              data={scatterData}
              fill={theme.palette.info.main}
              fillOpacity={0.85}
            />
            {regressionPoints && (
              <RechartsReferenceLine
                segment={[
                  { x: regressionPoints[0].x, y: regressionPoints[0].ry },
                  { x: regressionPoints[1].x, y: regressionPoints[1].ry },
                ]}
                stroke={theme.palette.warning.main}
                strokeDasharray="5 3"
                strokeWidth={1.5}
                label={{
                  value: "Trend",
                  position: "insideTopRight",
                  fontSize: 10,
                  fill: theme.palette.warning.main,
                }}
              />
            )}
          </RechartsScatterChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderSegmentedView = () => {
    if (comparisonLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 420 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (comparisonError) {
      return (
        <Alert severity="error">Failed to load comparison data. Please try again.</Alert>
      );
    }
    if (barChartData.length === 0) {
      return (
        <Alert severity="info">
          No comparison data available for the selected filters.
        </Alert>
      );
    }

    const segLabel =
      availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;

    return (
      <Box>
        {hiddenSegmentCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {hiddenSegmentCount} segment{hiddenSegmentCount !== 1 ? "s were" : " was"} hidden due to insufficient data.
          </Alert>
        )}
        <VerticalBarChart
          data={barChartData}
          xAxisKey={segLabel}
          series={[
            {
              dataKey: metricLabel(metricA),
              label: metricLabel(metricA),
              color: theme.palette.info.main,
            },
            {
              dataKey: metricLabel(metricB),
              label: metricLabel(metricB),
              color: theme.palette.success.main,
            },
          ]}
          height={420}
          yAxisMin={0}
        />
      </Box>
    );
  };

  const renderChart = () => {
    if (activeView === "segmented") return renderSegmentedView();
    return null; // "combined" is handled directly in the layout below
  };

  const chartTitle = () => {
    const segLabel =
      availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;
    if (activeView === "segmented")
      return `Segmented Comparison — ${segLabel}`;
    return `Trend & Correlation — ${metricLabel(metricA)} vs ${metricLabel(metricB)}`;
  };

  const chartSubtitle = () => {
    const segLabel =
      availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy;
    if (activeView === "segmented")
      return `Side-by-side averages grouped by ${segLabel}`;
    return `Over-time trend (left) and ${segLabel} correlation scatter with labels (right)`;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />

      <Stack spacing={3}>
        {/* ── Toolbar ────────────────────────────────────────────────────── */}
        <DashboardCard>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
              gap: 2,
            }}
          >
            {/* Metric selectors + segment */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flex: 1,
                flexWrap: "wrap",
              }}
            >
              <Autocomplete
                options={METRIC_OPTIONS.filter((m) => m.value !== metricB)}
                value={METRIC_OPTIONS.find((m) => m.value === metricA) ?? null}
                getOptionLabel={(opt) => opt.label}
                groupBy={(opt) => opt.group}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                onChange={(_e, newVal) => {
                  if (newVal && newVal.value !== metricB) setMetricA(newVal.value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Metric A"
                    size="small"
                    sx={{ minWidth: 220 }}
                  />
                )}
                sx={{ minWidth: 220 }}
              />
              <Autocomplete
                options={METRIC_OPTIONS.filter((m) => m.value !== metricA)}
                value={METRIC_OPTIONS.find((m) => m.value === metricB) ?? null}
                getOptionLabel={(opt) => opt.label}
                groupBy={(opt) => opt.group}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                onChange={(_e, newVal) => {
                  if (newVal) setMetricB(newVal.value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Metric B"
                    size="small"
                    sx={{ minWidth: 220 }}
                  />
                )}
                sx={{ minWidth: 220 }}
              />
              <Autocomplete
                options={availableSegmentOptions}
                value={availableSegmentOptions.find((s) => s.value === segmentBy) ?? availableSegmentOptions[0] ?? null}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                onChange={(_e, newVal) => {
                  if (newVal) handleSegmentByChange(newVal.value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Segment by"
                    size="small"
                  />
                )}
                sx={{ minWidth: 180 }}
              />
              {availableSegmentValues.length > 0 && (
                <Autocomplete
                  multiple
                  options={availableSegmentValues}
                  value={selectedSegmentValues}
                  onChange={(_e, newVal) => setSelectedSegmentValues(newVal)}
                  disableCloseOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Filter ${availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? "segments"}`}
                      size="small"
                      placeholder={selectedSegmentValues.length === 0 ? "All" : undefined}
                    />
                  )}
                  sx={{ minWidth: 240 }}
                />
              )}
            </Box>

            {/* Date + filter controls */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}>
              <DurationSelector
                value={dateRange}
                onChange={(range: DateRange) => setDateRange(range)}
                buttonVariant="outlined"
              />
              <Button
                variant="text"
                size="medium"
                startIcon={<FilterListIcon />}
                color={activeFiltersCount > 0 ? "primary" : "inherit"}
                onClick={() => setIsFilterOpen(true)}
              >
                {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
              </Button>
            </Box>
          </Box>
        </DashboardCard>

        {/* ── Summary stat cards ─────────────────────────────────────────── */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {statsLoading ? (
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, height: 96 }} />
            ) : (
              <SummaryStatCard
                label={`${metricLabel(metricA)} — Current Average`}
                value={statA?.value}
                unit={metricUnit(metricA)}
                change={statA?.change}
                color={theme.palette.info.main}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {statsLoading ? (
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, height: 96 }} />
            ) : (
              <SummaryStatCard
                label={`${metricLabel(metricB)} — Current Average`}
                value={statB?.value}
                unit={metricUnit(metricB)}
                change={statB?.change}
                color={theme.palette.success.main}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Correlation coefficient
              </Typography>
              {comparisonLoading ? (
                <CircularProgress size={18} />
              ) : correlation !== null ? (
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography variant="h5" fontWeight={700}>
                    {correlation.toFixed(3)}
                  </Typography>
                  <Chip
                    label={
                      Math.abs(correlation) >= 0.7
                        ? "Strong"
                        : Math.abs(correlation) >= 0.4
                          ? "Moderate"
                          : "Weak"
                    }
                    size="small"
                    color={
                      Math.abs(correlation) >= 0.7
                        ? "success"
                        : Math.abs(correlation) >= 0.4
                          ? "warning"
                          : "default"
                    }
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not enough data
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {metricLabel(metricA)} ↔ {metricLabel(metricB)}
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Active segment dimension
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {availableSegmentOptions.find((s) => s.value === segmentBy)?.label ?? segmentBy}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {comparisonData?.data?.groups?.length ?? "—"} groups in current period
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* ── Main chart area ────────────────────────────────────────────── */}
        {activeView === "combined" ? (
          /* Combined tab: trend full-width on top, scatter full-width below */
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              {trendsLoading ? (
                <DashboardCardSkeleton title subtitle height={420} />
              ) : (
                <DashboardCard
                  title="Trend"
                  subtitle={`${metricLabel(metricA)} vs ${metricLabel(metricB)} over time`}
                  titleComponent={
                    <ToggleButtonGroup value={activeView} exclusive onChange={handleViewChange} size="small">
                      <ToggleButton value="combined" aria-label="Trend & Correlation view">
                        <ShowChartIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Trend & Correlation
                      </ToggleButton>
                      <ToggleButton value="segmented" aria-label="Segmented view">
                        <BarChartIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Segmented
                      </ToggleButton>
                    </ToggleButtonGroup>
                  }
                >
                  <CohortGuard userCount={activeUserCount} bypass={isCentralAdmin || !isAnonymised} loading={cohortQuery.isLoading}>
                    {renderTrendView(380)}
                  </CohortGuard>
                </DashboardCard>
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              {comparisonLoading ? (
                <DashboardCardSkeleton title subtitle height={420} />
              ) : (
                <DashboardCard
                  title="Correlation"
                  subtitle={`${metricLabel(metricA)} vs ${metricLabel(metricB)} — hover dots to see ${SEGMENT_OPTIONS.find((s) => s.value === segmentBy)?.label ?? segmentBy}`}
                >
                  <CohortGuard userCount={activeUserCount} bypass={isCentralAdmin || !isAnonymised} loading={cohortQuery.isLoading}>
                    {renderScatterView(420)}
                  </CohortGuard>
                </DashboardCard>
              )}
            </Grid>
          </Grid>
        ) : (
          /* Segmented tab */
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              {comparisonLoading ? (
                <DashboardCardSkeleton title subtitle height={500} />
              ) : (
                <DashboardCard
                  title={chartTitle()}
                  subtitle={chartSubtitle()}
                  titleComponent={
                    <ToggleButtonGroup
                      value={activeView}
                      exclusive
                      onChange={handleViewChange}
                      size="small"
                    >
                      <ToggleButton value="combined" aria-label="Trend & Correlation view">
                        <ShowChartIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Trend & Correlation
                      </ToggleButton>
                      <ToggleButton value="segmented" aria-label="Segmented view">
                        <BarChartIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Segmented
                      </ToggleButton>
                    </ToggleButtonGroup>
                  }
                >
                  <CohortGuard
                    userCount={activeUserCount}
                    bypass={isCentralAdmin || !isAnonymised}
                    loading={cohortQuery.isLoading}
                  >
                    {renderChart()}
                  </CohortGuard>
                </DashboardCard>
              )}
            </Grid>
          </Grid>
        )}

      </Stack>
    </Box>
  );
};
